"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import type { CoverLetterData } from "@/lib/coverLetterEditor/types";
import { loadCoverLetter, saveCoverLetter } from "@/lib/coverLetterEditor/storage";
import { COVER_LETTER_TEMPLATES } from "@/lib/coverLetterEditor/templates";
import type { CoverLetterDraft } from "@/lib/platform/aiTools";

const DEFAULT_TEMPLATE = COVER_LETTER_TEMPLATES[0];
import IntakeModal from "@/components/coverLetterEditor/IntakeModal";
import CoverLetterEditorShell from "@/components/coverLetterEditor/CoverLetterEditorShell";

function newId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function CoverLetterBuilderInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const id = searchParams.get("id");

  const [data, setData] = useState<CoverLetterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Intake is opt-in (a card + button, not an auto-popup) so /cover-letter-builder
  // still reads as a normal landing page — the hero/FAQ around this component —
  // on first visit rather than immediately blocking with a modal.
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!id) return;
    const existing = loadCoverLetter(id);
    if (existing) {
      setData(existing);
    } else {
      setError("This cover letter draft wasn't found on this device.");
    }
  }, [id]);

  function handleGenerated(
    info: { jobTitle: string; company: string; fullName: string; email: string },
    draft: CoverLetterDraft
  ) {
    const now = new Date().toISOString();
    const newLetter: CoverLetterData = {
      id: newId(),
      fullName: info.fullName,
      email: info.email,
      jobTitle: info.jobTitle,
      recipientCompany: info.company,
      date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      salutation: draft.salutation,
      paragraphs: draft.paragraphs,
      closing: draft.closing,
      meta: {
        templateId: DEFAULT_TEMPLATE.id,
        accentColor: DEFAULT_TEMPLATE.accentColor,
        fontFamily: DEFAULT_TEMPLATE.fontFamily,
        createdAt: now,
        updatedAt: now,
      },
    };
    saveCoverLetter(newLetter);
    router.replace(`/cover-letter-builder?id=${newLetter.id}`);
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200/80 bg-rose-50/60 p-6 text-center text-sm text-rose-600 dark:border-rose-800/60 dark:bg-rose-500/5 dark:text-rose-400">
        {error}
      </div>
    );
  }

  if (id) {
    if (!data) {
      return (
        <div className="flex min-h-[40vh] items-center justify-center">
          <p className="text-slate-400">Loading editor…</p>
        </div>
      );
    }
    // Escapes the marketing page's max-w-3xl content column via fixed
    // positioning — this is a static-exported site (no request-time
    // server), so the editor can't be given its own full-bleed layout at
    // the server level like /resume-editor does. This achieves the same
    // full-viewport feel from a client component mounted anywhere.
    //
    // z-[60]: the site's global <Header> is `fixed top-0 z-50` (see
    // Header.tsx) — anything under z-50 here gets visually covered by it,
    // which is what made the editor's own toolbar buttons unreachable.
    // Deliberately not touching SiteChrome/root layout to fix this at the
    // source: SiteChrome wraps every page from the root layout, so adding
    // useSearchParams() there to conditionally hide the header would force
    // Next.js to bail the *entire* static-exported site into dynamic
    // rendering, which breaks `output: "export"` outright.
    return (
      <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-50 dark:bg-[#0b1220]">
        <CoverLetterEditorShell initialData={data} />
      </div>
    );
  }

  if (started) {
    return <IntakeModal onGenerated={handleGenerated} onClose={() => setStarted(false)} />;
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-8 text-center backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
      <p className="text-xl font-bold text-slate-900 dark:text-white">Ready to write your cover letter?</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">
        Tell us the job title, company and your key strengths — we&apos;ll draft the letter, then you fine-tune every word.
      </p>
      <button
        type="button"
        onClick={() => setStarted(true)}
        className="mt-5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-110"
      >
        Start Writing — Free
      </button>
    </div>
  );
}

export default function CoverLetterBuilderApp() {
  return (
    <Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center text-slate-400">Loading…</div>}>
      <CoverLetterBuilderInner />
    </Suspense>
  );
}
